import { css } from 'styled-components';

/**
 * App-owned CSS for Astryx surfaces — the things `inqTheme` cannot express.
 *
 * Everything here exists because `defineTheme` has two hard limits:
 *
 * 1. **`tokens` is a closed set.** Its type is `Partial<Record<TokenName, …>>`,
 *    so a bespoke `--color-*` name is a compile error. Custom values have to be
 *    declared as plain CSS.
 * 2. **`components` takes a flat map of CSS properties per state**, with no
 *    pseudo-selector support — so `:focus-within`, and any rule that has to
 *    reach a child element, cannot live in the theme.
 *
 * Kept out of `global.styles.tsx` so the Astryx-specific workarounds sit in one
 * place: each is load-bearing for a reason that isn't obvious from the rule
 * itself, and they should be revisited together whenever `@astryxdesign/core`
 * is upgraded — a future version may make some of them unnecessary.
 *
 * **Deliberately unlayered.** `global.styles.tsx` declares
 * `@layer inq-reboot, reset, astryx-base, astryx-theme`, and unlayered rules
 * beat every layer. That is what lets these override Astryx's own component
 * styles; putting them in a layer would make them lose.
 */
export const AstryxOverridesCss = css`
  /*
    Custom properties for Create AI, scoped to the Astryx subtree rather than
    :root.

    Astryx emits its own tokens inside \`@scope\` rules keyed on
    \`[data-astryx-theme]\`, so its tokens live and die with the themed subtree.
    Declaring these on the same element keeps them to the same lifetime and the
    same scope: custom properties inherit, so every Astryx descendant can read
    them, and nothing leaks to the rest of the app.

    They are named outside the \`--color-*\` namespace Astryx owns, so they can
    never collide with a real token — and \`webclient/CLAUDE.md\` forbids
    redefining \`--color-*\` anyway.

    Note these are static values: unlike a real theme token they take no
    [light, dark] pair, so they will not follow a mode switch. That is fine while
    \`inqTheme\` is light-only — revisit if a dark theme is ever added.
  */
  [data-astryx-theme] {
    /* Selected option row in the Create AI flow. */

    /* Flow progress bar. --color-success (green-8) is the dark green used for
       status text and reads far too heavy as a progress fill. */
    --inq-progress-fill: #73d13d; /* green-5 */
    --inq-progress-track: #f0f0f0;

    /* Quiet outline for the composer container. */
    --inq-border-subtle: #e8e8e8; /* grey-4 */

    /* Subject tints — the step-2 rung of each SubjectColours ramp in
       colours.style.tsx, keyed by the ramp name Subject.colour returns from
       startup data. Decorative panel fills behind a subject's illustration, with
       the subject name rendered beside the panel rather than on it, so nothing
       here carries meaning by colour alone. */
    --inq-subject-green: #d9f7be;
    --inq-subject-orange: #ffe7ba;
    --inq-subject-purple: #efdbff;
    --inq-subject-volcano: #ffd8bf;
    --inq-subject-gold: #fff1b8;
    --inq-subject-azure: #d6e4ff;
    --inq-subject-cyan: #b5f5ec;
    --inq-subject-magenta: #ffd6e7;
    --inq-subject-grey: #fafafa;
  }

  /*
    Purple focus ring on the Create AI composer.

    A theme override can't express it: the treatment is a :focus-within on the
    container, and component overrides carry no pseudo-selectors.
  */
  .astryx-chat-composer:focus-within {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-accent-muted);
  }

  /*
    One text weight across every create-type toggle, selected or not.

    Astryx shifts a pressed ToggleButton's label to semibold via a StyleX class
    on an inner span, so neither a theme override nor a prop can reach it — the
    child's own class wins. Both states are pinned rather than just the pressed
    one: overriding only the pressed state leaves the *unselected* buttons
    looking heavier than the selected one.
  */
  .astryx-toggle-button,
  .astryx-toggle-button span {
    font-weight: var(--font-weight-normal);
  }
`;
